/**
 * Redux duck for the AI search answer page /sok?fraga=… (P25009-447).
 *
 * The flow is deliberately split so the model can never invent stock (BR-3):
 *
 *   1. /api/ai-search interprets the question into FACETS — the marketplace's
 *      real filter keys, whitelisted server-side. The model sees the question,
 *      never the inventory.
 *   2. If the question names a car, /api/ai-search/car returns the car's
 *      versions from our own car database as quick choices, and which one to
 *      assume until the buyer picks (P25009-479 BR-2 / BR-4).
 *   3. The facets run as an ORDINARY Sharetribe listings query — the same call
 *      /annonser makes. Counts, sellers and prices are read off that response
 *      and nothing else.
 *   4. What the Marketplace API cannot filter is checked on the listings read:
 *      the whole-set price (BR-1) here, and the car's fit (BR-5 / BR-6) by
 *      /api/ai-search/fit, the same rule "Passar även" uses. The count is then
 *      the number of listings that passed, not the API's total.
 *
 * Facets are also the pills: removing a pill disables its facet and re-runs
 * steps 3–4 only. The interpretation (and its server-side cache) is untouched,
 * so a pill removal costs a search query, never a model call.
 *
 * loadData runs on SSR and the client, mirroring SeoPages.duck.js.
 */
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { apiBaseUrl, getLicensePlateInfo } from '../../util/api';
import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import { hasSalesUnit, resolveSalesUnit, setAmount, MAX_SET_SIZE } from '../../util/salesUnit';
import { boltCircleSpellings } from '../../util/boltCircleCore';
import { MARKETPLACE_FILTER } from '../../util/catalogueCore';

const { Money } = sdkTypes;

// Listings per Marketplace API page — the API ceiling.
const SCAN_PAGE_SIZE = 100;

/* How many pages to read when the answer depends on a check made after the
 * query (fit, set price). Every listing that could pass has to be read for
 * the count to be true; past this the page says "minst". The fit endpoint
 * accepts the same 500. */
const MAX_SCAN_PAGES = 5;

// How many cards the page shows when results are not grouped.
const RESULT_PAGE_SIZE = 12;

// Highest price filter the facet sends when the question only states a floor.
const PRICE_CEILING_SEK = 1000000;

// ================ Action types ================ //

export const INTERPRET_REQUEST = 'app/AISearchPage/INTERPRET_REQUEST';
export const INTERPRET_SUCCESS = 'app/AISearchPage/INTERPRET_SUCCESS';
export const INTERPRET_ERROR = 'app/AISearchPage/INTERPRET_ERROR';

export const QUERY_REQUEST = 'app/AISearchPage/QUERY_REQUEST';
export const QUERY_SUCCESS = 'app/AISearchPage/QUERY_SUCCESS';
export const QUERY_ERROR = 'app/AISearchPage/QUERY_ERROR';

export const TOGGLE_FACET = 'app/AISearchPage/TOGGLE_FACET';

export const SELECT_CAR = 'app/AISearchPage/SELECT_CAR';
export const SET_CAR_QUESTION_OPEN = 'app/AISearchPage/SET_CAR_QUESTION_OPEN';

export const PLATE_REQUEST = 'app/AISearchPage/PLATE_REQUEST';
export const PLATE_SUCCESS = 'app/AISearchPage/PLATE_SUCCESS';
export const PLATE_ERROR = 'app/AISearchPage/PLATE_ERROR';

// ================ Reducer ================ //

const initialState = {
  question: null,
  interpretation: null,
  // The car the question named, as /api/ai-search/car returned it:
  // { make, model, slugs, choices, assumedId, confident }.
  car: null,
  carChoiceId: null,
  /* Where the chosen version came from: 'assumed' (our guess — the page asks),
   * 'question' (the year in the question), 'only' (the model has one version),
   * 'buyer' (a quick choice) or 'plate' (a plate lookup). */
  carChoiceSource: null,
  carQuestionOpen: false,
  plate: null,
  plateLookupInProgress: false,
  plateLookupError: null,
  facets: [],
  disabledFacetIds: [],
  interpretInProgress: false,
  interpretError: null,
  queryInProgress: false,
  queryError: null,
  // The latest query; an older one that lands late must not overwrite it.
  queryToken: null,
  listingRefs: [],
  // Results by rim size, when the car's fit was checked (BR-5 / AC-5).
  groups: [],
  fitChecked: false,
  totalItems: 0,
  // True when more listings matched the query than the page could read and
  // check — the count is then a floor, and the page says "minst".
  totalIsAtLeast: false,
  sellerCount: 0,
  sellerCountIsAtLeast: false,
  // The cheapest whole set among the results (BR-1), as Money.
  fromSetPrice: null,
  // Question the data in state belongs to; a refetch of the same question
  // keeps the rendered data to avoid an SSR→hydration flash (same reasoning
  // as SeoPages.duck.js `loadedFor`).
  loadedFor: null,
};

export default function aiSearchPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case INTERPRET_REQUEST: {
      const isSameQuestion = payload.question != null && payload.question === state.loadedFor;
      return isSameQuestion
        ? { ...state, interpretInProgress: true, interpretError: null }
        : {
            ...initialState,
            question: payload.question,
            interpretInProgress: true,
          };
    }
    case INTERPRET_SUCCESS:
      return {
        ...state,
        interpretInProgress: false,
        interpretation: payload.interpretation,
        car: payload.car,
        carChoiceId: payload.carChoiceId,
        carChoiceSource: payload.carChoiceSource,
        carQuestionOpen: payload.carChoiceSource === 'assumed',
        plate: null,
        plateLookupError: null,
        facets: payload.facets,
        disabledFacetIds: [],
        loadedFor: state.question,
      };
    case INTERPRET_ERROR:
      return { ...state, interpretInProgress: false, interpretError: payload };
    case QUERY_REQUEST:
      return { ...state, queryInProgress: true, queryError: null, queryToken: payload.token };
    case QUERY_SUCCESS:
      if (payload.token !== state.queryToken) return state;
      return {
        ...state,
        queryInProgress: false,
        listingRefs: payload.listingRefs,
        groups: payload.groups,
        fitChecked: payload.fitChecked,
        totalItems: payload.totalItems,
        totalIsAtLeast: payload.totalIsAtLeast,
        sellerCount: payload.sellerCount,
        sellerCountIsAtLeast: payload.sellerCountIsAtLeast,
        fromSetPrice: payload.fromSetPrice,
      };
    case QUERY_ERROR:
      if (payload.token !== state.queryToken) return state;
      return { ...state, queryInProgress: false, queryError: payload.error };
    case TOGGLE_FACET: {
      const id = payload;
      const disabled = state.disabledFacetIds.includes(id)
        ? state.disabledFacetIds.filter(f => f !== id)
        : [...state.disabledFacetIds, id];
      return { ...state, disabledFacetIds: disabled };
    }
    case SELECT_CAR:
      return {
        ...state,
        car: payload.car,
        carChoiceId: payload.carChoiceId,
        carChoiceSource: payload.carChoiceSource,
        carQuestionOpen: payload.carChoiceSource === 'assumed',
        plate: payload.plate || null,
        facets: payload.facets,
        // Picking a version is asking for the car — a removed car pill returns.
        disabledFacetIds: state.disabledFacetIds.filter(id => id !== 'car'),
      };
    case SET_CAR_QUESTION_OPEN:
      return { ...state, carQuestionOpen: !!payload, plateLookupError: null };
    case PLATE_REQUEST:
      return { ...state, plateLookupInProgress: true, plateLookupError: null };
    case PLATE_SUCCESS:
      return { ...state, plateLookupInProgress: false };
    case PLATE_ERROR:
      return { ...state, plateLookupInProgress: false, plateLookupError: payload };
    default:
      return state;
  }
}

// ================ Action creators ================ //

const interpretRequest = question => ({ type: INTERPRET_REQUEST, payload: { question } });
const interpretSuccess = payload => ({ type: INTERPRET_SUCCESS, payload });
const interpretError = e => ({ type: INTERPRET_ERROR, error: true, payload: e });
const queryRequest = token => ({ type: QUERY_REQUEST, payload: { token } });
const querySuccess = payload => ({ type: QUERY_SUCCESS, payload });
const queryError = (token, e) => ({ type: QUERY_ERROR, error: true, payload: { token, error: e } });

export const setCarQuestionOpen = open => ({ type: SET_CAR_QUESTION_OPEN, payload: open });

// ================ Facets ================ //

const slugify = value =>
  String(value || '')
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[^a-z0-9_-]/g, '');

const AD_TYPE_LABELS = { wheel: 'Kompletta hjul', tire: 'Däck', rim: 'Fälgar' };
const SEASON_LABELS = {
  summer: 'Sommar',
  winter: 'Vinter',
  winterFriction: 'Vinter · friktion',
  winterDubbed: 'Vinter · dubb',
  rdack: 'R-däck',
  cdack: 'C-däck',
};

/* "winter" is the interpretation's umbrella for a buyer who did not choose
 * between studded and friction — the search asks for either (comma = OR). */
const SEASON_PARAMS = {
  winter: 'winterFriction,winterDubbed',
};
const CONDITION_LABELS = { new: 'Nytt', used: 'Begagnat' };

/* "20000" → "20 000 kr" with a non-breaking group space, formatted by hand so
 * SSR, browsers and jest agree on the exact string (toLocaleString's group
 * separator codepoint varies by ICU build). */
export const formatSek = n =>
  `${String(Math.round(Number(n))).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} kr`;

/** The chosen version of the car, or null. */
export const chosenCarChoice = state =>
  (state.car?.choices || []).find(c => c.id === state.carChoiceId) || null;

/**
 * The car facet's search params — fitment, never name (the same rule as
 * carSearchParams.js: pub_categoryLevel1/2 are rim-brand levels here, and a
 * car narrows by what physically fits).
 *
 * These only NARROW the query; the fit itself is decided per listing by
 * /api/ai-search/fit. Two things are therefore deliberately left out:
 *  - The offset. `etFront` is a long field, and the API neither indexes a
 *    decimal ET (Volvo's own 50.5 is never found by any range) nor includes
 *    the top of a range (40,55 drops every ET 55). The fit check reads it.
 *  - Locking on the car's first tyre size (BR-5). Every diameter the car is
 *    approved for is asked for, and every tyre width it runs; the ±3 %
 *    alternatives the rule accepts share the width.
 */
const carFacetParams = (choice, interpretation) => {
  const params = {};
  const { adType } = interpretation;

  if (adType === 'wheel' || adType === 'rim') {
    if (choice.boltHoles != null) params.pub_numberOfBolts = String(choice.boltHoles);
    if (choice.pcdMm != null) {
      /* Every stored spelling of the circle, as one OR: Console's key
       * ("114_3", and "1397" for 139.7) and the plain number older imports
       * wrote ("114.3") — P25009-488. */
      const pcd = boltCircleSpellings(choice.pcdMm).join(',');
      if (pcd) params.pub_boltCircle = pcd;
    }
  }

  // An explicit diameter in the question is its own facet and wins.
  if (choice.diameters?.length && !interpretation.diameter) {
    params.pub_diameter = choice.diameters.join(',');
  }

  /* Tyre widths only where the field holds a tyre's width: on rim listings
   * tireWidthFront has been seen holding the rim width. */
  if ((adType === 'wheel' || adType === 'tire') && choice.tireWidths?.length) {
    params.pub_tireWidthFront = choice.tireWidths.join(',');
  }

  return params;
};

const priceLabel = (min, max) => {
  if (min != null && max != null) return `${formatSek(min)} – ${formatSek(max)} per sats`;
  if (max != null) return `Max ${formatSek(max)} per sats`;
  return `Från ${formatSek(min)} per sats`;
};

/**
 * Interpretation (+ the chosen car version) → the facet list. Each facet is
 * one pill: an id, a Swedish label, and the search params it stands for.
 * Removing the pill removes exactly those params.
 *
 * Two facets carry a check the Marketplace API cannot make, and the query
 * thunk runs it on the listings read: `fitCarId` (the car) and `setPrice`.
 *
 * @param {Object} interpretation from /api/ai-search
 * @param {Object|null} carSelection { car, choice } — the car and its chosen version
 * @param {string} question the buyer's words
 */
export const buildFacets = (interpretation, carSelection, question) => {
  const facets = [];

  if (carSelection) {
    const { car, choice } = carSelection;
    facets.push({
      id: 'car',
      label: `${car.make} ${car.model}`,
      params: carFacetParams(choice, interpretation),
      fitCarId: choice.id,
    });
  }

  if (interpretation.adType) {
    facets.push({
      id: 'adType',
      label: AD_TYPE_LABELS[interpretation.adType],
      params: { pub_adType: interpretation.adType },
    });
  }

  if (interpretation.tiretype) {
    facets.push({
      id: 'season',
      label: SEASON_LABELS[interpretation.tiretype],
      params: {
        pub_tiretype: SEASON_PARAMS[interpretation.tiretype] || interpretation.tiretype,
      },
    });
  }

  if (interpretation.condition) {
    facets.push({
      id: 'condition',
      label: CONDITION_LABELS[interpretation.condition],
      params: { pub_condition: interpretation.condition },
    });
  }

  if (interpretation.diameter) {
    facets.push({
      id: 'diameter',
      label: `${interpretation.diameter}"`,
      params: { pub_diameter: String(interpretation.diameter) },
    });
  }

  /* BR-1 — "under 20 000" is the price of the whole set. The listing price is
   * the price of one sales unit (one wheel on most company listings), so the
   * facet carries the set bounds and the query thunk compares price × set
   * size. `params.price` keeps the /annonser URL form for the handoff. */
  const { priceMinSek, priceMaxSek } = interpretation;
  if (priceMinSek != null || priceMaxSek != null) {
    const min = priceMinSek ?? null;
    const max = priceMaxSek ?? null;
    facets.push({
      id: 'price',
      label: priceLabel(min, max),
      params: { price: `${min ?? 0},${max ?? PRICE_CEILING_SEK}` },
      setPrice: { min, max },
    });
  }

  if (interpretation.keywords) {
    const words = interpretation.keywords;
    facets.push({
      id: 'keywords',
      label: words.charAt(0).toUpperCase() + words.slice(1),
      params: { keywords: words },
    });
  }

  /* Nothing understood at all — fall back to a keyword search on the raw
   * question rather than an unfiltered query that would "answer" with the
   * whole marketplace. */
  if (facets.length === 0 && question) {
    facets.push({ id: 'keywords', label: question, params: { keywords: question } });
  }

  return facets;
};

const activeFacets = state => state.facets.filter(f => !state.disabledFacetIds.includes(f.id));

/** The merged search params of the currently active facets (URL form). */
export const activeFacetParams = state =>
  activeFacets(state).reduce((acc, f) => ({ ...acc, ...f.params }), {});

// ================ Checks made on the listings read ================ //

/** What one whole set of this listing costs, in subunits (BR-1), or null. */
export const setTotalAmount = listing => {
  const price = listing?.attributes?.price;
  if (price?.amount == null) return null;
  return setAmount(price.amount, resolveSalesUnit(listing.attributes.publicData || {}));
};

/** Is the whole-set price inside the buyer's bounds (whole SEK, inclusive)? */
export const withinSetPrice = (listing, { min, max }) => {
  const total = setTotalAmount(listing);
  if (total == null) return false;
  return (min == null || total >= min * 100) && (max == null || total <= max * 100);
};

/**
 * The API filters the listing price, which is the price of one sales unit. A
 * whole set costs that price times its set size, at most MAX_SET_SIZE times
 * it, so anything that can pass has a unit price in [min / MAX_SET_SIZE, max].
 * The query asks for that range; withinSetPrice makes the exact check.
 */
export const unitPriceRange = ({ min, max }) => {
  const low = min != null ? Math.floor(min / MAX_SET_SIZE) : 0;
  const high = max != null ? max : PRICE_CEILING_SEK;
  // Subunits, upper bound exclusive.
  return `${low * 100},${high * 100 + 1}`;
};

/**
 * BR-3 — only stock reaches the answer. The query already asks for the sell
 * listing types only; this is the second fence, for anything that slips past
 * it: a listing of another type, one without a price, or one that links away.
 * The sponsored cards ("admin-adverts") carry an externalLink — on Test they
 * are "big big wheel" and friends, pointing at google.com.
 */
export const isSellableListing = listing => {
  const { price, publicData = {} } = listing?.attributes || {};
  return hasSalesUnit(publicData) && price?.amount > 0 && !publicData.externalLink;
};

const diameterOf = listing => {
  const n = Number.parseFloat(
    String(listing?.attributes?.publicData?.diameter ?? '').replace(',', '.')
  );
  return Number.isFinite(n) ? n : null;
};

/** Results by rim size, smallest first (AC-5). */
export const groupBySize = listings => {
  const groups = new Map();
  listings.forEach(listing => {
    const diameter = diameterOf(listing);
    if (!groups.has(diameter)) groups.set(diameter, []);
    groups.get(diameter).push({ id: listing.id, type: 'listing' });
  });
  return [...groups.entries()]
    .sort(([a], [b]) => (a ?? Infinity) - (b ?? Infinity))
    .map(([diameter, listingRefs]) => ({ diameter, count: listingRefs.length, listingRefs }));
};

// ================ Thunks ================ //

const fetchInterpretation = (question, config) => {
  const base = apiBaseUrl(config?.marketplaceRootURL);
  return fetch(`${base}/api/ai-search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ question }),
  }).then(res => {
    if (!res.ok) {
      throw new Error(`ai-search interpretation failed with status ${res.status}`);
    }
    return res.json();
  });
};

/** The car's versions as quick choices, or null when the car is unknown. */
const fetchCar = (query, config) => {
  const base = apiBaseUrl(config?.marketplaceRootURL);
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') params.set(key, String(value));
  });
  return fetch(`${base}/api/ai-search/car?${params.toString()}`, {
    headers: { Accept: 'application/json' },
  })
    .then(res => (res.ok ? res.json() : null))
    .then(body => (body?.choices?.length ? body : null))
    .catch(() => null);
};

/* The fitment facts the rule reads — the same list /api/listing-fitment
 * takes, plus the listing type for listings that never got an adType. */
const FIT_FIELDS = [
  'adType',
  'listingType',
  'numberOfBolts',
  'boltCircle',
  'diameter',
  'etFront',
  'etRear',
  'hubHole',
  'rimWidthFront',
  'rimWidthRear',
  'tireWidthFront',
  'profileFront',
  'tireWidthRear',
  'profileRear',
];

/** Ids (uuid strings) of the listings that fit this car version. */
const fetchFit = (carId, listings, config) => {
  if (listings.length === 0) return Promise.resolve(new Set());
  const base = apiBaseUrl(config?.marketplaceRootURL);
  const body = {
    carId,
    listings: listings.map(l => {
      const publicData = l.attributes?.publicData || {};
      return {
        id: l.id.uuid,
        publicData: FIT_FIELDS.reduce((out, key) => {
          if (publicData[key] !== undefined) out[key] = publicData[key];
          return out;
        }, {}),
      };
    }),
  };
  return fetch(`${base}/api/ai-search/fit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  })
    .then(res => {
      if (!res.ok) throw new Error(`ai-search fit failed with status ${res.status}`);
      return res.json();
    })
    .then(json => new Set((json.results || []).filter(r => r.fits).map(r => r.id)));
};

/* The same buyer-visible listing types /annonser sells — never the sponsored
 * cards (admin-adverts), buying requests, or any other non-stock type the
 * hosted config holds (Test has "Test" and "forvaring"). */
const sellableListingTypes = config =>
  (config?.listing?.listingTypes || [])
    .map(l => l.listingType)
    .filter(listingType => hasSalesUnit({ listingType }))
    .join(',');

/**
 * Run the ordinary listings query for the active facets, then the checks the
 * API cannot make. This is the only source of every number the page states.
 */
export const queryGroundedListings = config => (dispatch, getState, sdk) => {
  const state = getState().AISearchPage;
  const facets = activeFacets(state);
  const params = activeFacetParams(state);
  const token = `${Date.now()}-${Math.random()}`;

  if (Object.keys(params).length === 0) {
    dispatch(queryRequest(token));
    dispatch(
      querySuccess({
        token,
        listingRefs: [],
        groups: [],
        fitChecked: false,
        totalItems: 0,
        totalIsAtLeast: false,
        sellerCount: 0,
        sellerCountIsAtLeast: false,
        fromSetPrice: null,
      })
    );
    return Promise.resolve();
  }

  dispatch(queryRequest(token));

  const fitCarId = facets.find(f => f.fitCarId)?.fitCarId || null;
  const setPrice = facets.find(f => f.setPrice)?.setPrice || null;
  /* Each is a check the Marketplace API cannot make, so the count has to come
   * from reading the listings rather than from its totalItems. */
  const checksAfterQuery = !!fitCarId || !!setPrice;

  // The facet's price is in /annonser's URL form; the query asks unit prices.
  const { price, ...rest } = params;
  const priceMaybe = setPrice ? { price: unitPriceRange(setPrice) } : {};

  /* The same projection the search page requests — the result cards read a
   * long list of publicData fields, and a field the query does not ask for
   * silently never renders on the card (learned the hard way on P25009-445).
   * The rear and rim-width fields are for the fit check, salesUnit / setSize /
   * isReachShell for the set price. */
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;

  const listingTypes = sellableListingTypes(config);
  const listingTypeMaybe = listingTypes ? { pub_listingType: listingTypes } : {};

  const queryParams = {
    ...rest,
    ...priceMaybe,
    ...listingTypeMaybe,
    pub_isDeleted: false,
    /* The same sellability guards /annonser applies (SearchPage.duck.js).
     * Without them the answer counts listings nobody can buy — out of stock,
     * reserved, or hidden — and states a bigger number than the "open as a
     * normal search" results will show. Stock the page may answer from is
     * stock we can sell (BR-3), so the two must agree. */
    minStock: 1,
    stockMode: 'match-undefined',
    pub_isReserved: false,
    pub_isHide: false,
    // P25009-410 — answer from ads, as /annonser does.
    ...MARKETPLACE_FILTER,
    perPage: SCAN_PAGE_SIZE,
    include: ['author', 'images', 'author.profileImage', 'currentStock'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'publicData.listingType',
      'publicData.externalLink',
      'publicData.subDescription',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
      'publicData.county',
      'publicData.isHide',
      'publicData.boltCircle',
      'publicData.numberOfBolts',
      'publicData.tireWidthFront',
      'publicData.tireWidthRear',
      'publicData.profileFront',
      'publicData.profileRear',
      'publicData.rimWidthFront',
      'publicData.rimWidthRear',
      'publicData.diameter',
      'publicData.threaddepth',
      'publicData.condition',
      'publicData.hubHole',
      'publicData.etFront',
      'publicData.etRear',
      'publicData.city',
      'publicData.updateDate',
      'publicData.totalLikes',
      'publicData.adType',
      'publicData.categoryLevel1',
      'publicData.categoryLevel2',
      'publicData.quantity',
      'publicData.brand',
      'publicData.sasong',
      'publicData.season',
      'publicData.tiretype',
      'publicData.salesUnit',
      'publicData.setSize',
      'publicData.isReachShell',
      'publicData.euLabel',
      'metadata',
    ],
    'fields.user': [
      'profile.displayName',
      'profile.bio',
      'profile.abbreviatedName',
      'profile.metadata',
      'profile.publicData.userType',
      'profile.publicData.services',
    ],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 3,
  };

  const readPage = page =>
    sdk.listings.query({ ...queryParams, page }).then(response => {
      dispatch(addMarketplaceEntities(response));
      return response;
    });

  return readPage(1)
    .then(first => {
      const totalPages = first.data.meta?.totalPages || 1;
      const lastPage = checksAfterQuery ? Math.min(totalPages, MAX_SCAN_PAGES) : 1;
      const more = [];
      for (let page = 2; page <= lastPage; page += 1) more.push(readPage(page));
      return Promise.all(more).then(responses => ({
        responses: [first, ...responses],
        readAll: lastPage >= totalPages,
      }));
    })
    .then(({ responses, readAll }) => {
      const read = responses.flatMap(r => r.data.data || []);
      const apiTotal = responses[0].data.meta?.totalItems ?? read.length;

      const candidates = read
        .filter(isSellableListing)
        .filter(l => !setPrice || withinSetPrice(l, setPrice));

      const fitPromise = fitCarId
        ? fetchFit(fitCarId, candidates, config)
        : Promise.resolve(null);

      return fitPromise.then(fitIds => {
        const matched = fitIds ? candidates.filter(l => fitIds.has(l.id.uuid)) : candidates;

        const totalItems = checksAfterQuery ? matched.length : apiTotal;
        const totalIsAtLeast = checksAfterQuery && !readAll;

        const sellerIds = new Set(
          matched.map(l => l.relationships?.author?.data?.id?.uuid).filter(Boolean)
        );

        const cheapest = matched.reduce((low, l) => {
          const total = setTotalAmount(l);
          return total != null && (low == null || total < low.total)
            ? { total, currency: l.attributes.price.currency }
            : low;
        }, null);

        dispatch(
          querySuccess({
            token,
            listingRefs: matched
              .slice(0, RESULT_PAGE_SIZE)
              .map(l => ({ id: l.id, type: 'listing' })),
            groups: fitIds ? groupBySize(matched) : [],
            fitChecked: !!fitIds,
            totalItems,
            totalIsAtLeast,
            sellerCount: sellerIds.size,
            sellerCountIsAtLeast: totalIsAtLeast || (!checksAfterQuery && apiTotal > read.length),
            fromSetPrice: cheapest ? new Money(cheapest.total, cheapest.currency) : null,
          })
        );
      });
    })
    .catch(e => {
      dispatch(queryError(token, storableError(e)));
    });
};

/** Pill removal / restore: flip the facet, requery. No model call. */
export const toggleFacet = (facetId, config) => dispatch => {
  dispatch({ type: TOGGLE_FACET, payload: facetId });
  return dispatch(queryGroundedListings(config));
};

const carSelectionOf = (car, choiceId) => {
  const choice = (car?.choices || []).find(c => c.id === choiceId);
  return choice ? { car, choice } : null;
};

/* Without a plate, a confident pick means one version, or the question's year
 * landing in exactly one (carChoices.pickChoice). */
const sourceOfPick = car => {
  if (!car) return null;
  if (!car.confident) return 'assumed';
  return car.choices.length === 1 ? 'only' : 'question';
};

/**
 * The buyer picks a version (a quick choice, BR-4). The car facet is rebuilt
 * for it and the stock is checked again; no model call.
 */
export const selectCarChoice = (choiceId, config, { car: nextCar, source, plate } = {}) => (
  dispatch,
  getState
) => {
  const state = getState().AISearchPage;
  const car = nextCar || state.car;
  const selection = carSelectionOf(car, choiceId);
  if (!selection) return Promise.resolve();
  dispatch({
    type: SELECT_CAR,
    payload: {
      car,
      carChoiceId: choiceId,
      carChoiceSource: source || 'buyer',
      plate,
      facets: buildFacets(state.interpretation || {}, selection, state.question),
    },
  });
  return dispatch(queryGroundedListings(config));
};

/**
 * "Ange regnummer" — the surest answer to "which XC60?". The plate lookup is
 * the REG mode's own (/api/get-license-plate-info), which resolves a plate to
 * a car database row; its model may differ from the one the question named,
 * and then the plate wins.
 */
export const lookupCarByPlate = (plateInput, config) => dispatch => {
  const plate = String(plateInput || '')
    .replace(/\s+/g, '')
    .toUpperCase();
  if (!plate) return Promise.resolve();

  dispatch({ type: PLATE_REQUEST });
  return getLicensePlateInfo({ licensePlate: plate })
    .then(res => {
      const exactId = res?.confidence === 'high' ? Number(res?.data?.[0]?.id) : NaN;
      /* "choose_series": the plate is a known model but not a known version.
       * Its versions become the quick choices, and the question stays open. */
      const seriesId =
        res?.status === 'choose_series' ? Number(res?.availableSeries?.[0]?.slug) : NaN;
      const carId = Number.isFinite(exactId) ? exactId : seriesId;
      if (!Number.isFinite(carId)) throw new Error('plate_not_resolved');
      return fetchCar({ carId }, config).then(car => {
        if (!car) throw new Error('plate_not_resolved');
        return { car, exact: Number.isFinite(exactId) };
      });
    })
    .then(({ car, exact }) => {
      dispatch({ type: PLATE_SUCCESS });
      return dispatch(
        selectCarChoice(car.assumedId, config, {
          car,
          source: exact ? 'plate' : 'assumed',
          plate,
        })
      );
    })
    .catch(e => {
      dispatch({ type: PLATE_ERROR, payload: storableError(e) });
    });
};

export const loadData = (params, search, config) => (dispatch, getState) => {
  const question = new URLSearchParams(String(search || '').replace(/^\?/, ''))
    .get('fraga')
    ?.trim();

  dispatch(interpretRequest(question || null));

  if (!question) {
    dispatch(
      interpretSuccess({
        interpretation: null,
        car: null,
        carChoiceId: null,
        carChoiceSource: null,
        facets: [],
      })
    );
    return Promise.resolve();
  }

  return fetchInterpretation(question, config)
    .then(body => {
      const interpretation = body?.interpretation || {};
      const { carMake, carModel, carYear } = interpretation;
      const carPromise =
        carMake && carModel
          ? fetchCar({ make: slugify(carMake), model: slugify(carModel), year: carYear }, config)
          : Promise.resolve(null);
      return carPromise.then(car => ({ interpretation, car }));
    })
    .then(({ interpretation, car }) => {
      const carChoiceId = car?.assumedId ?? null;
      const facets = buildFacets(interpretation, carSelectionOf(car, carChoiceId), question);
      dispatch(
        interpretSuccess({
          interpretation,
          car,
          carChoiceId,
          carChoiceSource: sourceOfPick(car),
          facets,
        })
      );
      return dispatch(queryGroundedListings(config));
    })
    .catch(e => {
      dispatch(interpretError(storableError(e)));
    });
};
